import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';

/**
 * Service for the "Order Stock" restocking flow (Deliverable 1: cart creation).
 *
 * Customer-facing reads/writes (list, detail, suggested cart, submit/update/cancel
 * preview) go to `api/v1/customer/restock-orders…`. Every failure surfaces as a
 * typed RestockOrderError — nothing falls back to local data. See
 * .context/restocking-backend-contract.md for the endpoint contract. State beyond
 * `preview` is driven by the concierge in C.Tidy; the client reads the result.
 *
 * The API carries money in cents; this service maps to dollars at the boundary so
 * the view models and `formatMoney()` stay in dollars. Status chip visuals live in
 * src/global.scss as `.restock-status-chip`; state labels come from `stateLabel()`
 * in ./restock-order-state.pipe.ts.
 */

export type RestockOrderState = 'preview' | 'finalizing' | 'ordered' | 'delivered' | 'restocked' | 'cancelled';

/**
 * Outcome of one restock attempt (a single order↔booking attachment):
 * `pending` until the assigned job is completed, then `restocked` (≥1 item
 * restocked) or `not_restocked` (the job happened but nothing was restocked).
 */
export type RestockAttemptState = 'pending' | 'restocked' | 'not_restocked';

/**
 * Any failure from the restock-orders backend — a business rejection (4xx/5xx
 * with a response) or a transport failure, which arrives as status 0. Callers
 * catch this to route the customer (e.g. `active_cart_exists` → Edit Cart) or
 * surface `message`.
 */
export class RestockOrderError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,           // stable server code, e.g. 'active_cart_exists'
    readonly existingOrderId?: number // set on active_cart_exists → the cart to edit
  ) {
    super(message);
    this.name = 'RestockOrderError';
    // Preserve instanceof across the TS/ES5 target downlevel.
    Object.setPrototypeOf(this, RestockOrderError.prototype);
  }
}

export interface BrandCandidate {
  rank: number;              // 1 = most preferred
  brand: string;             // 'Charmin'
  productName: string;       // 'Ultra Soft Mega, 12 rolls'
  sku: string;               // 'WM-88231'
  unitPrice: number;         // dollars per pack
  inStock: boolean;
  // Units in one pack (12 mega rolls), from the preferred-brand tier. Undefined
  // when the customer never set a pack size — the UI then counts packs only.
  unitsPerPack?: number;
}

export interface RestockCartLine {
  roomObjectId: number;
  name: string;              // 'Toilet Paper'
  icon: string;              // svg path
  level: 'low' | 'opportunistic' | 'ok';
  // Null when the customer hasn't configured the level yet — distinct from 0,
  // which is a real "none left" / "don't hold any". Rendered as "?".
  currentQuantity: number | null;
  minQuantity: number | null;
  restockToQuantity: number | null; // target level after restock (item's max quantity)
  orderQuantity: number;     // packs to order (stepper-controlled)
  maxUnitPrice: number;      // dollars — hard cap per pack
  candidates: BrandCandidate[];  // sorted by rank
}

/** A preview line is orderable only after its required restocking setup exists. */
export function isRestockCartLineReady(line: RestockCartLine): boolean {
  const hasPreferredBrand = (line.candidates || []).some((candidate) => !!candidate.brand?.trim());
  return hasPreferredBrand
    && Number(line.restockToQuantity) > 0
    && Number(line.maxUnitPrice) > 0;
}

/**
 * Short-lived UI state for a Create Order round trip through item details.
 * Lines are grouped exactly as the customer left them so returning does not
 * rebuild the cart from the default stock-level suggestions.
 */
export interface RestockCartDraft {
  propertyId: number;
  propertyName: string;
  editOrderId: number | null;
  lowLines: RestockCartLine[];
  opportunisticLines: RestockCartLine[];
  addedLines: RestockCartLine[];
  moreLines: RestockCartLine[];
}

export interface ResolvedCandidate {
  sku: string;
  brand: string;
  productName: string;
  unitPrice: number;
  substitutionNote?: string; // e.g. 'Charmin unavailable — using backup choice #2'
  // See BrandCandidate.unitsPerPack. The orders API doesn't return it, so pages
  // recover it from the room object's waterfall via unitsPerPackFor().
  unitsPerPack?: number;
}

export interface RestockOrderItem extends RestockCartLine {
  resolved: ResolvedCandidate;
  // Unit price of the 1st choice when the order fell through to a backup, so
  // the UI can strike it through next to the price actually paid.
  previousUnitPrice?: number;
}

export interface RestockTimelineEvent {
  label: string;             // 'Preview cart submitted'
  detail?: string;           // 'A TIDY Concierge will review and finalize your cart.'
  at: string;                // ISO datetime
}

export interface RestockOrder {
  id: number;
  propertyId: number;
  propertyName: string;
  vendor: string;            // 'Walmart Business'
  state: RestockOrderState;
  createdAt: string;         // ISO
  estimatedTotal: number;
  finalTotal?: number;       // tax-inclusive grand total, read back once placed
  subtotal?: number;         // pre-tax subtotal, from the placed-order readback
  tax?: number;              // tax, from the placed-order readback
  orderNumber?: string;      // vendor order # (Amazon/Walmart), set when ordered
  eta?: string;              // ISO date, set at ordered
  deliveryWindow?: string;   // delivery time-of-day window, e.g. '11am–1pm'
  trackingUrl?: string;      // set at ordered
  items: RestockOrderItem[];
  timeline: RestockTimelineEvent[];
  // Bookings this order is attached to, so a pro restocks it at those jobs.
  // Each attachment carries its own attempt outcome (`state`); a cancelled
  // booking's attachment is dropped by the backend. An order can attach to
  // several jobs over time. Detail-only — the list/summary shape omits it.
  assignedBookings: AssignedBooking[];
  // The customer's free-text note to the pro (empty when unset).
  proNote?: string;
  // The note actually shown to the pro — `proNote`, or one derived from the
  // order state when blank (e.g. "Delivered — should be at the front door").
  effectiveProNote?: string;
}

/**
 * One booking a restock order is attached to, from the order's point of view.
 * `state` is this attempt's outcome (pending / restocked / not_restocked).
 */
export interface AssignedBooking {
  bookingId: number;
  jobId?: number;
  date?: string;             // ISO date of the booking
  proName?: string;          // assigned pro(s), joined with ' & '
  state: RestockAttemptState;
}

/**
 * A restock task attached to a job, as it arrives nested in the customer
 * booking-details payload (`restock_tasks`). One entry per attached order.
 * Pending attempts carry the expected math; completed attempts carry read-only
 * per-item results for past-job pages. Shared by the job page and past pages.
 */
export interface RestockTaskItem {
  id: number;                          // RestockOrderItem id
  name: string;
  // The item's icon (room-object category icon), when the backend provides it;
  // pages fall back to a generic icon otherwise.
  icon?: string;
  currentQuantity: number | null;
  packsOrdered: number | null;
  unitsPerPack: number | null;
  expectedQuantity: number | null;
  // Completion results — null while the attempt is pending.
  restocked: boolean | null;
  restockedQuantity: number | null;
}

export interface RestockTask {
  id: number;                          // restock order id
  state: RestockAttemptState;
  effectiveProNote?: string;
  items: RestockTaskItem[];
}

/** Shared by the in-app task card and the past-job PDF renderer. */
export function restockTaskStatusLabel(state: RestockAttemptState): string {
  if (state === 'restocked') { return 'Restocked'; }
  if (state === 'not_restocked') { return 'Not Restocked'; }
  return 'Restocking Requested';
}

/**
 * The complete item detail shown in both past-job renderers. Keeping this in the
 * domain layer prevents the screen and exported PDF from drifting apart.
 */
export function restockTaskItemDetail(state: RestockAttemptState, item: RestockTaskItem): string {
  if (state === 'pending') {
    const parts: string[] = [];
    if (item.currentQuantity !== null && item.currentQuantity !== undefined) {
      parts.push(`${item.currentQuantity} on hand`);
    }
    if (item.packsOrdered !== null && item.packsOrdered !== undefined) {
      const packs = `${item.packsOrdered} ${item.packsOrdered === 1 ? 'pack' : 'packs'}`;
      parts.push(item.unitsPerPack ? `${packs} of ${item.unitsPerPack}` : packs);
    }
    if (item.expectedQuantity !== null && item.expectedQuantity !== undefined) {
      parts.push(`expect ${item.expectedQuantity}`);
    }
    return parts.join(' · ');
  }
  if (item.restocked) {
    const total = item.restockedQuantity ?? item.expectedQuantity;
    const parts = ['Restocked'];
    if (total !== null && total !== undefined) {
      parts.push(`${total} on hand`);
    }
    if (item.expectedQuantity !== null && item.expectedQuantity !== undefined && item.expectedQuantity !== total) {
      parts.push(`expected ${item.expectedQuantity}`);
    }
    return parts.join(' · ');
  }
  return 'Not restocked';
}

/**
 * Item-level slice of the newest in-flight order containing a room object —
 * `current_restock_order` from `room-objects?includes=with_restock_status`.
 * Null/absent when the item isn't in any active order (cycle complete).
 */
export interface RoomObjectRestockStatus {
  orderId: number;
  state: RestockOrderState;
  etaDate?: string;
  trackingUrl?: string;
  createdAt: string;
  orderQuantity: number;
  resolved?: ResolvedCandidate;
}

const TERMINAL_JOB_PROGRESS = new Set([
  'complete',
  'completed',
  'cancelled',
  'client_cancelled',
  'homekeeper_cancelled',
  'request_cancelled',
  'request_failed',
  'no_show',
  'homekeeper_no_show'
]);

/** Completed, cancelled, failed and no-show jobs cannot receive new restock work. */
export function isRestockJobAssignable(job: any): boolean {
  const progress = job?.job_progress
    ?? job?.status?.status
    ?? job?.status?.name
    ?? job?.status
    ?? '';
  const normalized = String(progress).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return !TERMINAL_JOB_PROGRESS.has(normalized);
}

/** Lowest-rank candidate that's in stock and within the price cap; annotates substitutions. */
function resolveCandidate(line: RestockCartLine): ResolvedCandidate {
  const ranked = [...line.candidates].sort((a, b) => a.rank - b.rank);
  const chosen = ranked.find((c) => c.inStock && c.unitPrice <= line.maxUnitPrice) || ranked[0];
  // A room object with no preferred-brand tiers (and no category default) comes back
  // with an empty candidate list — the concierge picks the brand later. Return an
  // empty resolution instead of crashing the whole cart on `chosen.sku`.
  if (!chosen) {
    return { sku: '', brand: '', productName: '', unitPrice: 0 };
  }
  const resolved: ResolvedCandidate = {
    sku: chosen.sku,
    brand: chosen.brand,
    productName: chosen.productName,
    unitPrice: chosen.unitPrice,
    unitsPerPack: chosen.unitsPerPack,
  };
  if (chosen.rank > 1) {
    resolved.substitutionNote = `${ranked[0].brand} unavailable — using backup choice #${chosen.rank}`;
  }
  return resolved;
}

// --- API contract (snake_case, money in cents) → domain mapping ---

interface ApiBrandCandidate {
  rank: number;
  brand: string;
  product_name: string;
  sku: string;
  unit_price_cents: number;
  in_stock: boolean;
}

interface ApiResolvedCandidate {
  sku: string;
  brand: string;
  product_name: string;
  unit_price_cents: number;
  substitution_note?: string;
}

interface ApiCartLine {
  room_object_id: number;
  name: string;
  icon?: string;
  level: 'low' | 'opportunistic' | 'ok';
  current_quantity: number;
  min_quantity: number;
  restock_to_quantity: number;
  order_quantity: number;
  max_unit_price_cents: number;
  candidates: ApiBrandCandidate[];
}

interface ApiOrderItem extends ApiCartLine {
  resolved: ApiResolvedCandidate;
}

interface ApiRestockOrder {
  id: number;
  address_id: number;
  property_name: string;
  vendor: string;
  state: RestockOrderState;
  created_at: string;
  estimated_total_cents: number;
  final_total_cents?: number;
  subtotal_cents?: number;
  tax_cents?: number;
  order_number?: string;
  eta_date?: string;
  delivery_window?: string;
  tracking_url?: string;
  items: ApiOrderItem[];
  timeline: RestockTimelineEvent[];
  assigned_bookings?: ApiAssignedBooking[];
  pro_note?: string;
  effective_pro_note?: string;
}

interface ApiAssignedBooking {
  booking_id: number;
  job_id?: number;
  date?: string;
  pro_name?: string;
  state: RestockAttemptState;
}

/** One entry of the `restock_tasks` array nested in the booking-details payload. */
export interface ApiRestockTask {
  id: number;
  state: RestockAttemptState;
  effective_pro_note?: string;
  items: ApiRestockTaskItem[];
}

interface ApiRestockTaskItem {
  id: number;
  name: string;
  icon_url?: string;
  icon?: string;
  current_quantity: number | null;
  packs_ordered: number | null;
  units_per_pack: number | null;
  expected_quantity: number | null;
  restocked: boolean | null;
  restocked_quantity: number | null;
}

export interface ApiRoomObjectRestockOrder {
  id: number;
  state: RestockOrderState;
  eta_date?: string;
  tracking_url?: string;
  created_at: string;
  order_quantity: number;
  resolved?: ApiResolvedCandidate;
}

interface ApiSuggestedCart {
  low: ApiCartLine[];
  opportunistic: ApiCartLine[];
  more: ApiCartLine[];
}

function centsToDollars(cents: number): number {
  return Math.round(cents || 0) / 100;
}

function dollarsToCents(dollars: number): number {
  return Math.round((dollars || 0) * 100);
}

function candidateFromApi(apiCandidate: ApiBrandCandidate): BrandCandidate {
  return {
    rank: apiCandidate.rank,
    brand: apiCandidate.brand,
    productName: apiCandidate.product_name,
    sku: apiCandidate.sku,
    unitPrice: centsToDollars(apiCandidate.unit_price_cents),
    inStock: apiCandidate.in_stock,
  };
}

function cartLineFromApi(line: ApiCartLine): RestockCartLine {
  return {
    roomObjectId: line.room_object_id,
    name: line.name,
    icon: line.icon || 'assets/svg/cube-outline.svg',
    level: line.level,
    currentQuantity: line.current_quantity,
    minQuantity: line.min_quantity,
    restockToQuantity: line.restock_to_quantity,
    orderQuantity: line.order_quantity,
    maxUnitPrice: centsToDollars(line.max_unit_price_cents),
    candidates: (line.candidates || []).map(candidateFromApi),
  };
}

function resolvedFromApi(resolved: ApiResolvedCandidate): ResolvedCandidate {
  return {
    sku: resolved.sku,
    brand: resolved.brand,
    productName: resolved.product_name,
    unitPrice: centsToDollars(resolved.unit_price_cents),
    substitutionNote: resolved.substitution_note,
  };
}

/** Maps a room-object's `current_restock_order` (or its absence) to the domain shape. */
export function roomObjectRestockStatusFromApi(dto: ApiRoomObjectRestockOrder | null | undefined): RoomObjectRestockStatus | null {
  if (!dto) {
    return null;
  }
  return {
    orderId: dto.id,
    state: dto.state,
    etaDate: dto.eta_date,
    trackingUrl: dto.tracking_url,
    createdAt: dto.created_at,
    orderQuantity: dto.order_quantity,
    resolved: dto.resolved ? resolvedFromApi(dto.resolved) : undefined,
  };
}

function orderItemFromApi(item: ApiOrderItem): RestockOrderItem {
  return { ...cartLineFromApi(item), resolved: resolvedFromApi(item.resolved) };
}

function assignedBookingFromApi(dto: ApiAssignedBooking): AssignedBooking {
  return {
    bookingId: dto.booking_id,
    jobId: dto.job_id,
    date: dto.date,
    proName: dto.pro_name,
    state: dto.state,
  };
}

function restockTaskItemFromApi(item: ApiRestockTaskItem): RestockTaskItem {
  return {
    id: item.id,
    name: item.name,
    icon: item.icon_url || item.icon || undefined,
    currentQuantity: item.current_quantity,
    packsOrdered: item.packs_ordered,
    unitsPerPack: item.units_per_pack,
    expectedQuantity: item.expected_quantity,
    restocked: item.restocked,
    restockedQuantity: item.restocked_quantity,
  };
}

/**
 * Maps a `restock_tasks` entry from the booking-details payload to the domain
 * shape. Exported so the job page and past-job pages — which load their own
 * booking-details response — render restock tasks from one mapping.
 */
export function restockTaskFromApi(task: ApiRestockTask): RestockTask {
  return {
    id: task.id,
    state: task.state,
    effectiveProNote: task.effective_pro_note,
    items: (task.items || []).map(restockTaskItemFromApi),
  };
}

function orderFromApi(order: ApiRestockOrder): RestockOrder {
  const hasFinal = order.final_total_cents !== undefined && order.final_total_cents !== null;
  const hasSubtotal = order.subtotal_cents !== undefined && order.subtotal_cents !== null;
  const hasTax = order.tax_cents !== undefined && order.tax_cents !== null;
  return {
    id: order.id,
    propertyId: order.address_id,
    propertyName: order.property_name,
    vendor: order.vendor,
    state: order.state,
    createdAt: order.created_at,
    estimatedTotal: centsToDollars(order.estimated_total_cents),
    finalTotal: hasFinal ? centsToDollars(order.final_total_cents) : undefined,
    subtotal: hasSubtotal ? centsToDollars(order.subtotal_cents) : undefined,
    tax: hasTax ? centsToDollars(order.tax_cents) : undefined,
    orderNumber: order.order_number,
    eta: order.eta_date,
    deliveryWindow: order.delivery_window,
    trackingUrl: order.tracking_url,
    items: (order.items || []).map(orderItemFromApi),
    timeline: order.timeline || [],
    assignedBookings: (order.assigned_bookings || []).map(assignedBookingFromApi),
    proNote: order.pro_note,
    effectiveProNote: order.effective_pro_note,
  };
}

/** Request body items for preview submit/update: ranked candidate SKUs + price cap per line. */
function toPreviewItemsPayload(
  lines: RestockCartLine[]
): { room_object_id: number; quantity: number; max_unit_price_cents: number; candidates: string[] }[] {
  return lines.map((line) => ({
    room_object_id: line.roomObjectId,
    quantity: line.orderQuantity,
    max_unit_price_cents: dollarsToCents(line.maxUnitPrice),
    // SKUs are unresolved until PMS (Phase 3), so candidates can be null — only send
    // real SKU strings, else the `Array[String]` param rejects the request.
    candidates: [...line.candidates]
      .sort((a, b) => a.rank - b.rank)
      .map((entry) => entry.sku)
      .filter((sku): sku is string => !!sku),
  }));
}

@Injectable({ providedIn: 'root' })
export class RestockingOrdersService {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api_url}api/v1/customer/restock-orders`;

  /** Cache of orders fetched from the backend, backing `orders$`. */
  private orders: RestockOrder[] = [];

  /** Consumed the next time Create Order is opened after visiting an item. */
  private cartDraft: RestockCartDraft | null = null;

  /** Emits the current order list (newest first); consumers subscribe for live updates. */
  orders$ = new BehaviorSubject<RestockOrder[]>(this.sortedCopy());

  private sortedCopy(): RestockOrder[] {
    return [...this.orders].sort((a, b) => {
      const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return diff !== 0 ? diff : b.id - a.id;
    });
  }

  private emit(): void {
    this.orders$.next(this.sortedCopy());
  }

  private cloneCartLine(line: RestockCartLine): RestockCartLine {
    return {
      ...line,
      candidates: (line.candidates || []).map((candidate) => ({ ...candidate }))
    };
  }

  private cloneCartDraft(draft: RestockCartDraft): RestockCartDraft {
    return {
      ...draft,
      lowLines: draft.lowLines.map((line) => this.cloneCartLine(line)),
      opportunisticLines: draft.opportunisticLines.map((line) => this.cloneCartLine(line)),
      addedLines: draft.addedLines.map((line) => this.cloneCartLine(line)),
      moreLines: draft.moreLines.map((line) => this.cloneCartLine(line))
    };
  }

  saveCartDraft(draft: RestockCartDraft): void {
    this.cartDraft = this.cloneCartDraft(draft);
  }

  /** Return and clear the draft so it cannot leak into a later fresh order. */
  takeCartDraft(): RestockCartDraft | null {
    const draft = this.cartDraft ? this.cloneCartDraft(this.cartDraft) : null;
    this.cartDraft = null;
    return draft;
  }

  clearCartDraft(): void {
    this.cartDraft = null;
  }

  /** Insert or replace an order in the cache (real-mode responses) and emit. */
  private upsert(order: RestockOrder): void {
    const idx = this.orders.findIndex((existing) => existing.id === order.id);
    if (idx >= 0) {
      this.orders[idx] = order;
    } else {
      this.orders.push(order);
    }
    this.emit();
  }

  // Normalize the gotidy error envelope ({ error: { message, invalid_params } })
  // into a typed RestockOrderError the pages can branch on.
  //
  // The global ErrorInterceptor unwraps every HttpErrorResponse into a plain
  // { code, message, err } object (err = the raw gotidy body) before it reaches
  // here, so we can't rely on `instanceof HttpErrorResponse`. Dig the envelope
  // out of whichever shape arrives — the interceptor's formatted object, or the
  // raw response when the interceptor is bypassed.
  private toRestockError(err: unknown): RestockOrderError {
    const http = err instanceof HttpErrorResponse ? err : undefined;
    const formatted = (err && typeof err === 'object' ? err : {}) as Record<string, any>;

    // The gotidy body ({ error: {...} }), reached either through the raw response
    // (`http.error`) or the interceptor's carried copy (`formatted.err`).
    const body = http?.error ?? formatted['err'] ?? formatted;
    const envelope = body?.error ?? body ?? {};
    const invalid = envelope?.invalid_params ?? {};

    return new RestockOrderError(
      http?.status ?? formatted['status'] ?? 0,
      envelope?.message || formatted['message'] || http?.message || 'Something went wrong with your restock cart.',
      invalid?.code ?? envelope?.code ?? formatted['code'],
      invalid?.existing_order_id
    );
  }

  // Every failure — transport or a server rejection — surfaces as a typed
  // RestockOrderError so the page can react (409 active-cart → Edit Cart,
  // 400 → show the message) or toast it. Nothing is silently absorbed.
  private async call<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (err) {
      throw this.toRestockError(err);
    }
  }

  getOrders(propertyId?: number): RestockOrder[] {
    const all = this.sortedCopy();
    return propertyId === undefined ? all : all.filter((order) => order.propertyId === propertyId);
  }

  getOrder(id: number): RestockOrder | undefined {
    return this.orders.find((order) => order.id === id);
  }

  /** Fetch the order list (optionally scoped to a property) into `orders$`. */
  async loadOrders(propertyId?: number): Promise<RestockOrder[]> {
    const query = propertyId !== undefined && propertyId !== null ? `?filters[address_id]=${propertyId}` : '';
    const dtos = await this.call(() => firstValueFrom(this.http.get<ApiRestockOrder[]>(`${this.baseUrl}${query}`)));
    this.orders = (dtos || []).map(orderFromApi);
    this.emit();
    return this.getOrders(propertyId);
  }

  /** Full order (items + timeline) for the detail page; refreshes the cache. */
  async getOrderDetail(id: number): Promise<RestockOrder | undefined> {
    const dto = await this.call(() => firstValueFrom(this.http.get<ApiRestockOrder>(`${this.baseUrl}/${id}`)));
    const order = orderFromApi(dto);
    this.upsert(order);
    return order;
  }

  /** Preview resolution for a single cart line (build/edit cart) — same rules as submit. */
  resolveLine(line: RestockCartLine): ResolvedCandidate {
    return resolveCandidate(line);
  }

  /**
   * Newest in-flight order containing the room object + its line, from the
   * loaded cache. Mirrors the backend's `current_restock_order` semantics
   * (in-flight = everything but restocked/cancelled, so delivered counts —
   * the cycle only completes once items are put away).
   */
  getCurrentOrderForRoomObject(
    roomObjectId: number,
    propertyId?: number
  ): { order: RestockOrder; item: RestockOrderItem } | undefined {
    const inFlight: RestockOrderState[] = ['preview', 'finalizing', 'ordered', 'delivered'];
    // getOrders is newest-first, so the first match is the current order.
    for (const order of this.getOrders(propertyId)) {
      if (!inFlight.includes(order.state)) {
        continue;
      }
      const item = order.items.find((line) => line.roomObjectId === roomObjectId);
      if (item) {
        return { order, item };
      }
    }
    return undefined;
  }

  /** Pre-filled cart: low + opportunistic auto-added; `more` = addable extras. */
  async getSuggestedCart(addressId: number): Promise<{ low: RestockCartLine[]; opportunistic: RestockCartLine[]; more: RestockCartLine[] }> {
    // The endpoint requires a positive address_id; an empty param is a guaranteed
    // 400, so treat a missing property as an empty cart rather than a failed call.
    if (!addressId) {
      return { low: [], opportunistic: [], more: [] };
    }
    const dto = await this.call(() =>
      firstValueFrom(this.http.get<ApiSuggestedCart>(`${this.baseUrl}/suggested-cart?address_id=${addressId}`))
    );
    return {
      low: (dto.low || []).map(cartLineFromApi),
      opportunistic: (dto.opportunistic || []).map(cartLineFromApi),
      more: (dto.more || []).map(cartLineFromApi),
    };
  }

  async submitPreview(propertyId: number, lines: RestockCartLine[]): Promise<RestockOrder> {
    const dto = await this.call(() =>
      firstValueFrom(this.http.post<ApiRestockOrder>(`${this.baseUrl}/preview`, { address_id: propertyId, items: toPreviewItemsPayload(lines) }))
    );
    const created = orderFromApi(dto);
    this.upsert(created);
    return created;
  }

  async updatePreviewCart(orderId: number, lines: RestockCartLine[]): Promise<RestockOrder> {
    const dto = await this.call(() =>
      firstValueFrom(this.http.put<ApiRestockOrder>(`${this.baseUrl}/${orderId}`, { items: toPreviewItemsPayload(lines) }))
    );
    const updated = orderFromApi(dto);
    this.upsert(updated);
    return updated;
  }

  async cancelOrder(orderId: number): Promise<void> {
    const dto = await this.call(() =>
      firstValueFrom(this.http.post<ApiRestockOrder>(`${this.baseUrl}/${orderId}/cancel`, {}))
    );
    this.upsert(orderFromApi(dto));
  }

  /**
   * Attach the order to a booking so the pro restocks it at that job. Only
   * `ordered`/`delivered` orders are assignable. Returns the refreshed order
   * (with `assignedBookings`) and updates the cache. See card gCUZuCjf.
   */
  async assignBooking(orderId: number, bookingId: number): Promise<RestockOrder> {
    const dto = await this.call(() =>
      firstValueFrom(this.http.post<ApiRestockOrder>(`${this.baseUrl}/${orderId}/assign-booking`, { booking_id: bookingId }))
    );
    const order = orderFromApi(dto);
    this.upsert(order);
    return order;
  }

  /** Detach a pending restock attempt from a booking; returns the refreshed order. */
  async unassignBooking(orderId: number, bookingId: number): Promise<RestockOrder> {
    const dto = await this.call(() =>
      firstValueFrom(this.http.post<ApiRestockOrder>(`${this.baseUrl}/${orderId}/unassign-booking`, { booking_id: bookingId }))
    );
    const order = orderFromApi(dto);
    this.upsert(order);
    return order;
  }

  /** Set (or clear, when blank) the customer's note to the pro for this order. */
  async updateProNote(orderId: number, proNote: string): Promise<RestockOrder> {
    const dto = await this.call(() =>
      firstValueFrom(this.http.put<ApiRestockOrder>(`${this.baseUrl}/${orderId}/pro-note`, { pro_note: proNote }))
    );
    const order = orderFromApi(dto);
    this.upsert(order);
    return order;
  }
}
